/**
 * UI Store - 管理 UI 相關狀態（側邊欄、深色模式等）
 *
 * 此 store 獨立於 globalState，專門處理 UI 相關的狀態管理
 */

import { defineStore } from 'pinia'
import { eventBus } from './globalState'

// ==================== 常數 ====================

const DARK_MODE_STORAGE_KEY = 'app-dark-mode'

// ==================== 類型定義 ====================

export interface UIStoreState {
  /** 側邊欄是否收合 */
  sidebarCollapsed: boolean
  /** 是否為深色模式 */
  isDarkMode: boolean
}

// ==================== 輔助函數 ====================

/**
 * 讀取初始深色模式狀態
 * 優先從 localStorage 讀取，否則根據系統偏好
 */
function getInitialDarkMode(): boolean {
  const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY)
  if (stored !== null) {
    return stored === 'true'
  }
  // 根據系統偏好
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

// ==================== Store 定義 ====================

export const useUIStore = defineStore('uiStore', {
  state: (): UIStoreState => ({
    sidebarCollapsed: localStorage.getItem('sidebar_collapsed') === 'true',
    isDarkMode: getInitialDarkMode(),
  }),

  actions: {
    /**
     * 切換側邊欄展開/收合
     */
    toggleSidebar(): void {
      this.sidebarCollapsed = !this.sidebarCollapsed
      localStorage.setItem('sidebar_collapsed', String(this.sidebarCollapsed))
    },

    /**
     * 設定深色模式（統一入口）
     * @param isDark - 是否啟用深色模式
     */
    setDarkMode(isDark: boolean): void {
      // 更新 store 狀態
      this.isDarkMode = isDark

      // 保存到 localStorage
      localStorage.setItem(DARK_MODE_STORAGE_KEY, String(isDark))

      // 更新 body class
      if (isDark) {
        document.body.classList.add('dark-theme')
      } else {
        document.body.classList.remove('dark-theme')
      }

      // 更新 VXE Table 主題
      document.documentElement.setAttribute('data-vxe-ui-theme', isDark ? 'dark' : 'light')

      // 發出事件
      eventBus.emit('ui:theme-change', isDark)
    },

    /**
     * 切換深色模式
     */
    toggleDarkMode(): void {
      this.setDarkMode(!this.isDarkMode)
    },

    /**
     * 初始化深色模式（根據系統偏好或 localStorage）
     */
    initDarkMode(): void {
      this.setDarkMode(this.isDarkMode)
    },
  },
})

export default useUIStore
